import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactMapGL, { Source, Marker, Layer, FlyToInterpolator, NavigationControl, Popup } from "react-map-gl";
import GpsIcon from "@material-ui/icons/GpsFixed";
import Box from "@material-ui/core/Box";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import MarkerHouse from "assets/markers/house.png";
import MarkerTruck from "assets/markers/truck.png";
import Markers, { BlueTooltip } from "./Markers";
import {
  getDeliveryStartPathwayIndex,
  getStopLabel,
  resolvePathwayStopLngLat,
  resolveAllRouteStopsWithOffsets,
  formatStopTypeWithLabel,
  normalizeRoutePathway,
} from "util/stopLabelUtils";
import {
  collectOrdersForStop,
  getDeliveryStatus,
  getStartHouseVisual,
  getPathwayStopCompletedAt,
} from "util/stopStatusUtils";
import { getStopOrdersBreakdownLines } from "util/pathwayOrderUtils";
import { getRouteVrpIdentifier } from "util/routeDisplayUtils";
import mapboxgl from "mapbox-gl";
import polyline from "@mapbox/polyline";
import { useDispatch } from "react-redux";
import { getDriverLocations } from "redux/slices/routeSlice";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import { useTranslation } from "react-i18next";
import { useMapLanguage } from "hooks/useMapLanguage";
import axios from "axios";
import ColorPicker from "components/Shared/ColorPicker";

import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker?worker';
import { StartHouseIcon } from "../TableExpansionPanel";
mapboxgl.workerClass = MapboxWorker;

const MAPBOX_API_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_API_ACCESS_TOKEN;
const MAPBOX_MAP_STYLE = import.meta.env.VITE_MAPBOX_MAP_STYLE;

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hexToHsl(hex) {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) return null;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return rgbToHsl(r, g, b);
}

function parseToHsl(color) {
  if (!color) return null;
  const cleaned = color.trim();

  const hslMatch = cleaned.match(/hsla?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*(?:,\s*[\d.]+\s*)?\)/i);
  if (hslMatch) {
    return {
      h: parseFloat(hslMatch[1]),
      s: parseFloat(hslMatch[2]),
      l: parseFloat(hslMatch[3]),
    };
  }

  const rgbMatch = cleaned.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+\s*)?\)/i);
  if (rgbMatch) {
    return rgbToHsl(parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10));
  }

  return hexToHsl(cleaned);
}

function getVrpColor(baseColor, vehicleIndex) {
  const hsl = parseToHsl(baseColor);
  if (!hsl) return baseColor;

  const i = parseInt(vehicleIndex, 10) || 0;
  const dir = i % 2 === 0 ? 1 : -1;
  const step = Math.floor((i + 1) / 2);

  let newL = hsl.l + dir * step * 12;
  let newS = hsl.s - dir * step * 8;
  let newH = (hsl.h + i * 5) % 360;

  newL = Math.max(15, Math.min(85, newL));
  newS = Math.max(15, Math.min(100, newS));

  return `hsl(${newH}, ${newS}%, ${newL}%)`;
}

const MAP_INITIAL_LATITUDE = 52.516453830917456;
const MAP_INITIAL_LONGITUDE = 13.377655445343715;

function splitDriverPathByDistance(locations, maxDistanceMeters = 200) {
  if (!locations.length) return [];
  const segments = [];
  let currentSegment = [locations[0]];
  function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  for (let i = 1; i < locations.length; i++) {
    const prev = locations[i - 1];
    const curr = locations[i];
    const dist = haversine(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
    if (dist > maxDistanceMeters) {
      if (currentSegment.length > 1) segments.push(currentSegment);
      currentSegment = [curr];
    } else {
      currentSegment.push(curr);
    }
  }
  if (currentSegment.length > 1) segments.push(currentSegment);
  return segments;
}

export default ({
  routes,
  highlightRoute,
  highlightedRouteId,
  openStop,
  user,
  selectedStop,
  estimated_route,
  socket,
  initialLat,
  initialLng,
  supplierLocations,
}) => {
  const ref = useRef(null);
  const mapboxRef = useRef(null);
  const dispatch = useDispatch();
  const hasEnhancedNavigation = !!user?.permissions?.routes?.enhanced_navigation;
  const [total, setTotal] = useState(0);
  const [driverLocations, setDriverLocations] = useState({});
  const [viewport, setViewport] = useState(() => ({
    width: "100%",
    height: "100%",
    latitude: Number.isFinite(Number(initialLat)) ? Number(initialLat) : 52.52,
    longitude: Number.isFinite(Number(initialLng)) ? Number(initialLng) : 13.405,
    zoom: 14,
  }));
  const [userMovedMap, setUserMovedMap] = useState(false);
  const prevRoutesLength = useRef(routes.length);
  const [showDriverPath, setShowDriverPath] = useState(true);
  const [showRealTimePath, setShowRealTimePath] = useState(true);
  const [showNavigation, setShowNavigation] = useState(true);
  const [showEstimated, setShowEstimated] = useState(true);
  const [selectedLegendTour, setSelectedLegendTour] = useState(null);
  const [customColors, setCustomColors] = useState({});
  const [colorPicker, setColorPicker] = useState({ open: false, routeId: null, type: null, tempColor: "" });
  const [lineHoverInfo, setLineHoverInfo] = useState(null);

  const [realTimeLocations, setRealTimeLocations] = useState({});
  const [temporaryPathData, setTemporaryPathData] = useState({});
  const [lastDriverLocationTs, setLastDriverLocationTs] = useState(null);
  const [isRealtimeActive, setIsRealtimeActive] = useState(false);

  const [truckImages, setTruckImages] = useState({});
  const [truckImagesOpenFor, setTruckImagesOpenFor] = useState(null);
  const [truckImagesLoading, setTruckImagesLoading] = useState(false);

  const fetchTruckImages = useCallback(async (routeId) => {
    if (!routeId) return;
    const URL = import.meta.env.VITE_API;

    if (truckImages[routeId]) return truckImages[routeId];
    setTruckImagesLoading(true);
    try {
      const res = await axios.get(`${URL}routes/${routeId}/truck-images`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = res.data;
      setTruckImages((prev) => ({ ...prev, [routeId]: data }));
      return data;
    } catch (err) {
      console.error("Failed to fetch truck images", err);
      setTruckImages((prev) => ({ ...prev, [routeId]: [] }));
      return [];
    } finally {
      setTruckImagesLoading(false);
    }
  }, [truckImages]);

  const handleOpenTruckImages = useCallback(async (routeId) => {
    await fetchTruckImages(routeId);
    setTruckImagesOpenFor(routeId);
  }, [fetchTruckImages]);

  const handleCloseTruckImages = useCallback(() => setTruckImagesOpenFor(null), []);

  const { t, i18n } = useTranslation();

  useMapLanguage(mapboxRef);

  const originCoordinates = useMemo(() => {
    if (routes.length > 0 && routes[0].origin_coordinates) {
      return {
        latitude: routes[0].origin_coordinates.coordinates[1],
        longitude: routes[0].origin_coordinates.coordinates[0],
      };
    }
    return user?.SupplierLocation?.coordinates
      ? {
        latitude: user.SupplierLocation.coordinates.coordinates[1],
        longitude: user.SupplierLocation.coordinates.coordinates[0],
      }
      : null;
  }, [routes, user?.SupplierLocation?.coordinates]);

  const onMarkerClick = useCallback(
    (routeId, stopIndex) => {
      openStop(routeId, stopIndex);
    },
    [openStop]
  );

  const viewportRafRef = useRef(null);
  const pendingViewportRef = useRef(null);
  const flyToClearRef = useRef(null);

  const flyTo = useCallback((latitude, longitude, options = {}) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    if (viewportRafRef.current) {
      cancelAnimationFrame(viewportRafRef.current);
      viewportRafRef.current = null;
    }
    if (flyToClearRef.current) {
      clearTimeout(flyToClearRef.current);
      flyToClearRef.current = null;
    }

    setViewport((v) => {
      const hasValidStart = Number.isFinite(v?.latitude) && Number.isFinite(v?.longitude);
      const duration = hasValidStart
        ? (typeof options.duration === "number" ? options.duration : 800)
        : 0;

      if (duration > 0) {
        flyToClearRef.current = setTimeout(() => {
          flyToClearRef.current = null;
          setViewport((prev) => ({
            ...prev,
            transitionDuration: 0,
            transitionInterpolator: undefined,
          }));
        }, duration + 50);
      }

      return {
        ...v,
        latitude,
        longitude,
        zoom: typeof options.zoom === "number" ? options.zoom : (v.zoom || 14),
        transitionDuration: duration,
        transitionInterpolator: duration > 0 ? new FlyToInterpolator() : undefined,
      };
    });
  }, []);

  useEffect(() => {
    if (selectedStop) return;
    if (userMovedMap) return;
    if (originCoordinates && Number.isFinite(originCoordinates.latitude) && Number.isFinite(originCoordinates.longitude)) {
      flyTo(originCoordinates.latitude, originCoordinates.longitude, { zoom: 14, duration: 800 });
    }
  }, [originCoordinates, selectedStop, userMovedMap, flyTo]);

  const handleViewportChange = useCallback((nextViewport, interactionState) => {
    const isInteracting = !!(
      interactionState?.isDragging ||
      interactionState?.isPanning ||
      interactionState?.isZooming ||
      interactionState?.isRotating
    );

    if (isInteracting) {
      setUserMovedMap(true);
      if (flyToClearRef.current) {
        clearTimeout(flyToClearRef.current);
        flyToClearRef.current = null;
      }
    }

    pendingViewportRef.current = {
      ...nextViewport,
      ...(isInteracting ? { transitionDuration: 0, transitionInterpolator: undefined } : {}),
    };

    if (viewportRafRef.current) return;
    viewportRafRef.current = requestAnimationFrame(() => {
      viewportRafRef.current = null;
      if (pendingViewportRef.current) {
        setViewport((v) => ({
          ...v,
          ...pendingViewportRef.current,
        }));
      }
    });
  }, []);

  const handleTransitionEnd = useCallback(() => {
    if (flyToClearRef.current) {
      clearTimeout(flyToClearRef.current);
      flyToClearRef.current = null;
    }
    setViewport((v) => ({
      ...v,
      transitionDuration: 0,
      transitionInterpolator: undefined,
    }));
  }, []);

  useEffect(() => {
    return () => {
      if (viewportRafRef.current) {
        cancelAnimationFrame(viewportRafRef.current);
        viewportRafRef.current = null;
      }
      if (flyToClearRef.current) {
        clearTimeout(flyToClearRef.current);
        flyToClearRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (routes.length !== prevRoutesLength.current) {
      setUserMovedMap(false);
      prevRoutesLength.current = routes.length;
    }
  }, [routes.length]);

  const routeIdsKey = useMemo(() => {
    return (routes || [])
      .map((r) => r?.id)
      .filter(Boolean)
      .sort((a, b) => Number(a) - Number(b))
      .join(",");
  }, [routes]);

  const fetchedRouteIdsRef = useRef(new Set());

  useEffect(() => {
    const currentRouteIds = (routes || []).map((r) => r?.id).filter(Boolean);
    const currentSet = new Set(currentRouteIds.map(Number));

    // Clean up removed routes from cache ref
    fetchedRouteIdsRef.current.forEach((id) => {
      if (!currentSet.has(Number(id))) {
        fetchedRouteIdsRef.current.delete(id);
      }
    });

    // Remove obsolete routes from driverLocations state
    setDriverLocations((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!currentSet.has(Number(id))) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    const idsToFetch = currentRouteIds.filter((id) => !fetchedRouteIdsRef.current.has(id));
    if (idsToFetch.length === 0) return;

    let isMounted = true;
    const fetchLocations = async () => {
      idsToFetch.forEach((id) => fetchedRouteIdsRef.current.add(id));
      for (const id of idsToFetch) {
        try {
          const routeLocations = await dispatch(getDriverLocations(id));
          if (isMounted) {
            setDriverLocations((prev) => ({
              ...prev,
              [id]: routeLocations || [],
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch driver locations for route ${id}:`, error);
          if (isMounted) {
            setDriverLocations((prev) => ({
              ...prev,
              [id]: [],
            }));
          }
        }
      }
    };

    fetchLocations();

    return () => {
      isMounted = false;
    };
  }, [routeIdsKey, dispatch]);

  useEffect(() => {
    console.log("socket", socket);
    if (!socket) return;
    console.log("[Map] Socket instance:", socket);
    const handleConnect = () => console.log("[Map] Socket connected");
    const handleDisconnect = () => console.log("[Map] Socket disconnected");
    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    const handleDriverLocation = (data) => {
      console.log("[Map] Received driver-location:", data);
      setLastDriverLocationTs(Date.now());
      const { routeId, latitude, longitude, timestamp } = data;
      setRealTimeLocations((prev) => ({
        ...prev,
        [routeId]: { latitude, longitude, timestamp },
      }));
      setTemporaryPathData((prev) => {
        const current = prev[routeId] || { points: [] };
        const newLocation = { latitude, longitude, timestamp };
        const newPoints = [...current.points, newLocation].slice(-120);
        return {
          ...prev,
          [routeId]: {
            points: newPoints,
          },
        };
      });
    };
    socket.on("driver-location", handleDriverLocation);
    const handleRouteUpdated = (data) => {
      console.log("[Map] Received route-updated:", data);
      setTimeout(async () => {
        if (data.id) {
          try {
            const routeLocations = await dispatch(getDriverLocations(data.id));
            setDriverLocations((prev) => ({
              ...prev,
              [data.id]: routeLocations,
            }));
            setTemporaryPathData((prev) => {
              const copy = { ...prev };
              delete copy[data.id];
              return copy;
            });
          } catch (error) {
            console.error(`Failed to fetch driver locations for route ${data.id}:`, error);
          }
        }
      }, 1000);
    };
    const handleRouteStopSkipped = (data) => {
      console.log("[Map] Received route-stop-skipped:", data);
    };
    socket.on("route-updated", handleRouteUpdated);
    socket.on("route-stop-skipped", handleRouteStopSkipped);
    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("driver-location", handleDriverLocation);
      socket.off("route-updated", handleRouteUpdated);
      socket.off("route-stop-skipped", handleRouteStopSkipped);
    };
  }, [socket, dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setRealTimeLocations((prev) => {
        const now = Date.now();
        const thirtySecondsAgo = now - 30000;

        const filtered = {};
        Object.entries(prev).forEach(([routeId, location]) => {
          if (location.timestamp && location.timestamp > thirtySecondsAgo) {
            filtered[routeId] = location;
          }
        });

        return filtered;
      });
    }, 30000);

    return () => clearTimeout(timeout);
  }, [realTimeLocations]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTemporaryPathData((prev) => {
        const now = Date.now();
        const xMinutesAgo = now - 180000;
        const filtered = {};
        Object.entries(prev).forEach(([routeId, data]) => {
          const points = (data.points || []).filter((p) => p.timestamp && p.timestamp > xMinutesAgo);
          if (points.length > 0) {
            filtered[routeId] = { points };
          }
        });
        return filtered;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!userMovedMap && !selectedStop && routes.length && routes.length !== total) {
      const normRoute = normalizeRoutePathway(routes[0]);
      const firstStop = normRoute?.pathway?.[0];
      const coords = firstStop ? resolvePathwayStopLngLat(firstStop, normRoute, 0, supplierLocations) : null;
      let latitude = coords?.latitude;
      let longitude = coords?.longitude;

      const routeDriverLocations = driverLocations[routes[0].id];
      if (routeDriverLocations && routeDriverLocations.length > 0) {
        const lastLocation = routeDriverLocations[routeDriverLocations.length - 1];
        latitude = lastLocation.latitude;
        longitude = lastLocation.longitude;
      } else if (routes[0]?.DriversLocations?.length) {
        const last = routes[0].DriversLocations[routes[0].DriversLocations.length - 1];
        latitude = last.location.coordinates[1];
        longitude = last.location.coordinates[0];
      }

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        flyTo(latitude, longitude, { zoom: 14, duration: 800 });
      }
    }

    if (routes.length !== total) {
      setTotal(routes.length);
    }
  }, [routes, total, userMovedMap, selectedStop, driverLocations, supplierLocations, flyTo]);

  const lastFlownStopRef = useRef(null);

  useEffect(() => {
    if (!selectedStop) {
      lastFlownStopRef.current = null;
      return;
    }
    const targetStop = selectedStop.customer || selectedStop.supplierLocation;
    if (!targetStop) return;

    const stopKey = `${selectedStop.route?.id}_${selectedStop.index}_${selectedStop.type}`;
    if (lastFlownStopRef.current === stopKey) return;

    const coords = resolvePathwayStopLngLat(
      targetStop,
      selectedStop.route || routes[0],
      selectedStop.index,
      supplierLocations
    );

    if (coords && Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)) {
      lastFlownStopRef.current = stopKey;
      flyTo(coords.latitude, coords.longitude, { zoom: 14, duration: 800 });
    }
  }, [selectedStop, supplierLocations, routes, flyTo]);

  const getColor = (routeId, type, defaultColor) => {
    if (type === "realTime") {
      return customColors[routeId]?.realTime || "#B266FF";
    }
    if (type === "estimated") {
      if (customColors[routeId]?.estimated) {
        return customColors[routeId].estimated;
      }
      const route = (routes || []).find((r) => String(r.id) === String(routeId));
      if (route) {
        const tourColor = typeof route.Tour?.color === "string" && route.Tour.color.trim()
          ? route.Tour.color.trim()
          : null;
        if (!tourColor) {
          return defaultColor;
        }
        if (route.vrp_info != null) {
          const vehicleIndex = typeof route.vrp_info === "object" ? route.vrp_info.vehicle_index : route.vrp_info;
          return getVrpColor(tourColor, vehicleIndex);
        }
        return tourColor;
      }
    }
    return customColors[routeId]?.[type] || defaultColor;
  };

  const layerStyle = useMemo(
    () => ({
      id: "line",
      type: "line",
      paint: {
        "line-color": [
          "case",
          ["has", "routeId"],
          [
            "coalesce",
            [
              "get",
              ["to-string", ["get", "routeId"]],
              [
                "literal",
                Object.fromEntries(routes.map((r) => [String(r.id), getColor(r.id, "navigation", "#6F9CEB")])),
              ],
            ],
            "#6F9CEB",
          ],
          "#6F9CEB",
        ],
        "line-width": 6,
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
    }),
    [routes, customColors]
  );

  const estimatedRouteLayerStyle = useMemo(
    () => ({
      id: "estimated-route",
      type: "line",
      paint: {
        "line-color": [
          "case",
          ["has", "routeId"],
          [
            "coalesce",
            [
              "get",
              ["to-string", ["get", "routeId"]],
              [
                "literal",
                Object.fromEntries(routes.map((r) => [String(r.id), getColor(r.id, "estimated", "#ff9900")])),
              ],
            ],
            "#ff9900",
          ],
          "#ff9900",
        ],
        "line-width": 4,
        "line-opacity": 0.8,
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
    }),
    [routes, customColors]
  );

  const driverPathLayerStyle = useMemo(
    () => ({
      id: "driver-path",
      type: "line",
      paint: {
        "line-color": [
          "case",
          ["has", "routeId"],
          [
            "coalesce",
            [
              "get",
              ["to-string", ["get", "routeId"]],
              ["literal", Object.fromEntries(routes.map((r) => [String(r.id), getColor(r.id, "driver", "#A259FF")]))],
            ],
            "#A259FF",
          ],
          "#A259FF",
        ],
        "line-width": 6,
        "line-opacity": 0.9,
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
    }),
    [routes, customColors]
  );

  const realTimePathLayerStyle = useMemo(
    () => ({
      id: "real-time-path",
      type: "line",
      paint: {
        "line-color": "#B266FF",
        "line-width": 6,
        "line-opacity": 0.9,
        "line-dasharray": [2, 2],
      },
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
    }),
    []
  );


  const onHover = useCallback(
    (e) => {
      if (e.features && e.features.length) {
        const lineFeature = e.features.find(
          (f) =>
            f.layer?.id === "line" ||
            f.layer?.id === "estimated-route" ||
            f.layer?.id === "estimated-route-hit" ||
            f.layer?.id === "driver-path" ||
            f.layer?.id === "real-time-path" ||
            f.source === "navigation" ||
            f.source === "estimated-route" ||
            f.source === "driver-path" ||
            f.source === "real-time-path"
        );

        if (lineFeature) {
          const rawId =
            lineFeature.properties?.routeId ??
            lineFeature.properties?.id ??
            lineFeature.id ??
            (routes.length === 1 ? routes[0]?.id : null);

          if (rawId != null || routes.length === 1) {
            if (rawId != null) {
              highlightRoute(rawId);
            }
            const matchedRoute =
              routes.find(
                (r) => String(r.id) === String(rawId) || String(r.uuid) === String(rawId)
              ) || (routes.length === 1 ? routes[0] : null);

            if (matchedRoute) {
              const lng = Array.isArray(e.lngLat) ? e.lngLat[0] : e.lngLat?.lng ?? e.lngLat?.longitude;
              const lat = Array.isArray(e.lngLat) ? e.lngLat[1] : e.lngLat?.lat ?? e.lngLat?.latitude;
              if (lng != null && lat != null) {
                setLineHoverInfo({
                  route: matchedRoute,
                  latitude: lat,
                  longitude: lng,
                });
                return;
              }
            }
          }
        }
      }
      highlightRoute(null);
      setLineHoverInfo(null);
    },
    [routes, highlightRoute]
  );

  const renderStartStopTooltip = (startStop, route, startIndex) => {
    if (!startStop) return startStop?.name || route?.Tour?.name || `Start ${route?.id}`;

    const stopStatus = getDeliveryStatus({
      stop: startStop,
      routeStarted: !!route?.start_date,
      routeCompleted: !!route?.end_date || route?.progress === "Complete",
      route,
      pathwayIndex: startIndex,
    });

    const stopDisplayName = startStop.name || route?.SupplierLocation?.name || route?.Tour?.name || t("Start Location");
    const stopType = formatStopTypeWithLabel(startStop, route, startIndex, t) || t("Start Location");
    const completedAt = getPathwayStopCompletedAt(startStop);

    const orders = collectOrdersForStop(startStop);
    const breakdownLines = getStopOrdersBreakdownLines(orders, t, { isFixStop: startStop?.id == null });

    const statusLabel = {
      skipped: t("Stop Skipped"),
      delivered: t("Completed"),
      partial: t("Partial"),
      unsuccessful: t("Unsuccessful"),
      order_removed: t("Order Removed"),
      ready: route?.start_date ? t("In Progress") : t("Ready"),
      in_progress: t("In Progress"),
    }[stopStatus] || t("In Progress");

    return (
      <div>
        <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#000" }}>
          {stopDisplayName}
        </Typography>

        <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
          <strong>{t("Stop Type")}:</strong> {stopType}
        </Typography>

        {completedAt ? (
          <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{t("Completed")}:</strong>{" "}
            {new Date(completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        ) : (
          (startStop.eta || startStop.eta_display) && (
            <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
              <strong>{t("ETA")}:</strong>{" "}
              {startStop.eta_display ||
                new Date(startStop.eta).toLocaleString([], {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
            </Typography>
          )
        )}

        {breakdownLines && breakdownLines.length > 0 && breakdownLines.map((line, index) => (
          <Typography key={line.kind || index} variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{line.label}:</strong> {t("orders")} {line.orders}, {t("packages")} {line.packages}
          </Typography>
        ))}

        <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
          <strong>{t("Status")}:</strong> {statusLabel}
        </Typography>
      </div>
    );
  };

  const centerSupplier = useCallback(() => {
    if (user?.SupplierLocation?.coordinates) {
      const coordinates = user.SupplierLocation.coordinates;
      flyTo(coordinates.coordinates[1], coordinates.coordinates[0], { duration: 1000 });
    }
  }, [user?.SupplierLocation?.coordinates, flyTo]);

  const estimatedRoutesGeometry = useMemo(() => {
    const features = routes
      .filter((route) => route.estimated_route?.routes?.[0]?.geometry)
      .map((route) => {
        return {
          type: "Feature",
          id: route.id,
          properties: { routeId: route.id },
          geometry: {
            type: "LineString",
            coordinates: polyline
              .decode(route.estimated_route.routes[0].geometry)
              .map(([lat, lng]) => [lng / 10, lat / 10]),
          },
        };
      });
    return features.length > 0 ? { type: "FeatureCollection", features } : null;
  }, [routes]);

  const driverPathGeometry = useMemo(() => {
    const allDriverPaths = [];
    Object.entries(driverLocations).forEach(([routeId, locations]) => {
      if (locations && locations.length > 1) {
        const segments = splitDriverPathByDistance(locations, 200);
        segments.forEach((segment) => {
          const coordinates = segment.map((location) => [location.longitude, location.latitude]);
          allDriverPaths.push({
            type: "Feature",
            id: routeId,
            properties: { routeId: routeId },
            geometry: { type: "LineString", coordinates },
          });
        });
      }
    });
    return allDriverPaths.length > 0 ? { type: "FeatureCollection", features: allDriverPaths } : null;
  }, [driverLocations]);

  const temporaryPathGeometry = useMemo(() => {
    const allTemporaryPaths = [];
    Object.entries(temporaryPathData).forEach(([routeId, data]) => {
      let points = data.points || [];
      // Do not filter out repeated coordinates for real-time path
      if (points.length < 2) return;
      const coordinates = points.map((point) => [point.longitude, point.latitude]);
      if (coordinates.length > 1) {
        allTemporaryPaths.push({
          type: "Feature",
          id: routeId,
          properties: { routeId: routeId, isTemporary: true },
          geometry: { type: "LineString", coordinates },
        });
      }
    });
    return allTemporaryPaths.length > 0 ? { type: "FeatureCollection", features: allTemporaryPaths } : null;
  }, [temporaryPathData, driverLocations]);

  const navigationSourceData = useMemo(
    () => ({
      type: "FeatureCollection",
      features: routes.reduce((acc, route) => {
        const arr = route.RoutesNavigations.map((rn) => ({
          type: "Feature",
          id: route.id,
          properties: {
            routeId: route.id,
            color: highlightedRouteId === route.id ? "#6F9CEB" : "#cdddf8",
          },
          geometry: {
            type: "LineString",
            coordinates: rn.navigation?.routes[0].geometry.coordinates,
          },
        }));
        return acc.concat(arr);
      }, []),
    }),
    [routes, highlightedRouteId]
  );

  const activeInteractiveLayers = useMemo(() => {
    const layers = [];
    if (hasEnhancedNavigation && showNavigation && navigationSourceData) {
      layers.push("line");
    }
    if (showEstimated && estimatedRoutesGeometry) {
      layers.push("estimated-route-hit", "estimated-route");
    }
    if (hasEnhancedNavigation && showDriverPath && driverPathGeometry) {
      layers.push("driver-path");
    }
    if (hasEnhancedNavigation && isRealtimeActive && showRealTimePath && temporaryPathGeometry) {
      layers.push("real-time-path");
    }
    return layers;
  }, [
    hasEnhancedNavigation,
    showNavigation,
    navigationSourceData,
    showEstimated,
    estimatedRoutesGeometry,
    showDriverPath,
    driverPathGeometry,
    isRealtimeActive,
    showRealTimePath,
    temporaryPathGeometry,
  ]);

  const hasStopAtOrigin = useMemo(() => {
    if (!originCoordinates || !routes.length) return false;
    return routes.some(
      (route) =>
        route.pathway &&
        route.pathway.some(
          (stop) => stop.latitude === originCoordinates.latitude && stop.longitude === originCoordinates.longitude
        )
    );
  }, [originCoordinates, routes]);

  const MARKER_OFFSET = 0.00015;

  const fitBoundsKeyRef = useRef(null);

  useEffect(() => {
    return () => {
      if (ref.current) {
        const map = ref.current.getMap();
        if (map) {
          map.remove();
        }
      }
    };
  }, []);

  useEffect(() => {
    const routesKey = routes.map((r) => r.id).join(",");
    if (fitBoundsKeyRef.current === routesKey) return;
    if (selectedStop) return;
    if (userMovedMap) return;
    if (routes.length > 1 && ref.current) {
      const allCoords = [];
      routes.forEach((route) => {
        if (Array.isArray(route.pathway)) {
          route.pathway.forEach((stop) => {
            if (stop && typeof stop.latitude === "number" && typeof stop.longitude === "number") {
              allCoords.push([stop.longitude, stop.latitude]);
            }
          });
        }
      });
      if (allCoords.length > 0) {
        const map = ref.current.getMap();
        const bounds = allCoords.reduce(
          (b, coord) => b.extend(coord),
          new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
        );
        map.fitBounds(bounds, { padding: 60, duration: 1000 });
        fitBoundsKeyRef.current = routesKey;
      }
    }
  }, [routes, selectedStop, userMovedMap]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (lastDriverLocationTs && Date.now() - lastDriverLocationTs < 8000) {
        setIsRealtimeActive(true);
      } else {
        setIsRealtimeActive(false);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lastDriverLocationTs]);

  // Language updates are handled by `useMapLanguage(mapboxRef)`.

  const LegendItem = ({ color, label, visible, onToggle, onColorClick }) => (
    <Box display="flex" alignItems="center" mb={2} justifyContent="center" style={{ opacity: visible ? 1 : 0.5 }}>
      <Box
        width={20}
        height={20}
        borderRadius="50%"
        bgcolor={color}
        mr={2}
        style={{ cursor: "pointer", boxShadow: "0 0 2px #000" }}
        onClick={onColorClick}
      />
      <Typography
        variant="body2"
        style={{ color: "#fff", cursor: "pointer", fontWeight: 500, minWidth: 120, textAlign: "center" }}
        onClick={onToggle}
      >
        {t(label)}
      </Typography>
    </Box>
  );

  return (
    <ReactMapGL
      {...viewport}
      onViewportChange={handleViewportChange}
      onTransitionEnd={handleTransitionEnd}
      onTransitionInterrupt={handleTransitionEnd}
      mapStyle={MAPBOX_MAP_STYLE}
      mapboxApiAccessToken={MAPBOX_API_ACCESS_TOKEN}
      onHover={onHover}
      ref={ref}
      onLoad={(event) => {
        mapboxRef.current = event.target;
      }}
      interactiveLayerIds={activeInteractiveLayers}
    >
      {routes.map((route, idx) => {
        const startIndex = getDeliveryStartPathwayIndex(route);
        const startStop = startIndex >= 0 ? route.pathway?.[startIndex] : null;
        const allResolvedStops = resolveAllRouteStopsWithOffsets(route, supplierLocations);
        const startResolved = allResolvedStops.find((s) => s.isStart);
        const startCoords =
          startResolved || (startStop && resolvePathwayStopLngLat(startStop, route, startIndex));

        if (!startCoords) return null;

        const startStopStatus = startStop
          ? getDeliveryStatus({
            stop: startStop,
            routeStarted: !!route?.start_date,
            routeCompleted: !!route?.end_date || route?.progress === "Complete",
            route,
            pathwayIndex: startIndex,
          })
          : null;

        const startHouseVisual = getStartHouseVisual(startStopStatus, {
          routeStarted: !!route?.start_date,
          deliveryStartStop: startStop,
          deliveryStartIndex: startIndex,
          context: "map",
        });

        return (
          <Marker
            key={`start-marker-${route.id || idx}`}
            latitude={startCoords.latitude}
            longitude={startCoords.longitude}
          >
            <BlueTooltip
              arrow
              placement="top"
              title={renderStartStopTooltip(startStop, route, startIndex)}
            >
              <div style={{ position: "relative", textAlign: "center" }}>
                <div
                  style={{
                    position: "absolute",
                    bottom: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: "bold",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                    marginBottom: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {getStopLabel(route, startIndex) || "L1"}
                </div>
                <div
                  style={{ cursor: "pointer", display: "block" }}
                >
                  <StartHouseIcon
                    filled={startHouseVisual.filled}
                    fill={startHouseVisual.fill}
                    size={28}
                    bgFill="#1F1F1F"
                  />
                </div>
              </div>
            </BlueTooltip>
          </Marker>
        );
      })}

      {routes.map((route, i) => (
        <Markers route={route} onClick={onMarkerClick} key={route.id || i} supplierLocations={supplierLocations} />
      ))}

      {routes.map((route, i) => {
        if (!route.start_date || route.end_date) return null;

        const tempData = temporaryPathData[route.id];
        const tempPoints = tempData && tempData.points && tempData.points.length > 0 ? tempData.points : null;
        let lastLocation = null;
        let isRealTime = false;

        if (tempPoints) {
          const lastPoint = tempPoints[tempPoints.length - 1];
          lastLocation = {
            latitude: lastPoint.latitude,
            longitude: lastPoint.longitude,
          };
          isRealTime = true;
        } else {
          const routeDriverLocations = driverLocations[route.id];
          if (Array.isArray(routeDriverLocations) && routeDriverLocations.length > 0) {
            lastLocation = routeDriverLocations[routeDriverLocations.length - 1];
          } else if (route.DriversLocations && route.DriversLocations.length > 0) {
            const last = route.DriversLocations[route.DriversLocations.length - 1];
            lastLocation = {
              latitude: last.location.coordinates[1],
              longitude: last.location.coordinates[0],
            };
          }
        }

        if (!lastLocation) return null;

        return (
          <Marker key={`driver-${route.id || i}`} latitude={lastLocation.latitude} longitude={lastLocation.longitude}>
            <img
              alt="icon"
              src={MarkerTruck}
              style={{
                opacity: isRealTime ? 1 : 0.7,
                filter: isRealTime ? "none" : "grayscale(30%)",
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          </Marker>
        );
      })}

      {hasEnhancedNavigation && showNavigation && (
        <Source id="navigation" type="geojson" data={navigationSourceData}>
          <Layer {...layerStyle} />
        </Source>
      )}

      {showEstimated && estimatedRoutesGeometry && (
        <Source id="estimated-route" type="geojson" data={estimatedRoutesGeometry}>
          <Layer
            id="estimated-route-hit"
            type="line"
            paint={{
              "line-width": 16,
              "line-opacity": 0,
            }}
          />
          <Layer {...estimatedRouteLayerStyle} />
        </Source>
      )}

      {hasEnhancedNavigation && showDriverPath && driverPathGeometry && (
        <Source id="driver-path" type="geojson" data={driverPathGeometry}>
          <Layer {...driverPathLayerStyle} />
        </Source>
      )}

      {hasEnhancedNavigation && isRealtimeActive && showRealTimePath && temporaryPathGeometry && (
        <Source id="real-time-path" type="geojson" data={temporaryPathGeometry}>
          <Layer {...realTimePathLayerStyle} />
        </Source>
      )}

      {lineHoverInfo && lineHoverInfo.route && (() => {
        const vrpText = getRouteVrpIdentifier(lineHoverInfo.route) ?? (lineHoverInfo.route?.vrp_info ? "VRP" : null);

        return (
          <Popup
            latitude={lineHoverInfo.latitude}
            longitude={lineHoverInfo.longitude}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offsetTop={-10}
            className="blue-line-popup"
          >
            <div style={{ color: "#000", lineHeight: "1.4", pointerEvents: "none" }}>
              {vrpText && (
                <Typography variant="body2" style={{ fontWeight: "bold", color: "#000", fontSize: "12px", marginBottom: "2px" }}>
                  {vrpText}
                </Typography>
              )}
              <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#000" }}>
                T{lineHoverInfo.route.uuid}
              </Typography>
              {lineHoverInfo.route.Tour?.name && (
                <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
                  {lineHoverInfo.route.Tour.name}
                </Typography>
              )}
            </div>
          </Popup>
        );
      })()}

      <NavigationControl style={{ bottom: 30, right: 20, backgroundColor: "red" }} showCompass={false} />
      <Box
        position="absolute"
        bottom={85}
        right={20}
        px={0.3}
        pt={0.5}
        style={{ cursor: "pointer" }}
        bgcolor="white"
        borderRadius={3}
        onClick={centerSupplier}
      >
        <GpsIcon />
      </Box>

      <Dialog open={!!truckImagesOpenFor} onClose={handleCloseTruckImages} maxWidth="md" height="5%">
        <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('Photos of Loaded Vehicle')}</Typography>
          <IconButton size="small" onClick={handleCloseTruckImages}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: 5 }}>
          {truckImagesLoading && (
            <Typography>{t("Loading...")}</Typography>
          )}

          {!truckImagesLoading && truckImagesOpenFor && (
            <Box
              height={"50%"}
            >
              {(truckImages[truckImagesOpenFor] || []).length === 0 && (
                <Typography>{t("No images available")}</Typography>
              )}

              {(truckImages[truckImagesOpenFor] || []).map((img) => (
                <Box
                  key={img.id}
                  style={{
                    background: "#f7f7f7",
                    borderRadius: 6,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                  }}
                >
                  <img
                    src={img.url}
                    alt={img.filename || img.id}
                    style={{
                      width: "50%",
                      height: "50%",
                      objectFit: "cover",
                      borderRadius: 6,
                      cursor: "pointer"
                    }}
                    onClick={() => window.open(img.url, "_blank")}
                  />

                  <Typography variant="caption" style={{ marginTop: 8, wordBreak: "break-all" }}>
                    {img.filename}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {hasEnhancedNavigation && routes && routes.length > 0 && (
        <Box
          position="absolute"
          top={20}
          right={20}
          bgcolor="#111"
          borderRadius={2}
          p={2}
          boxShadow={2}
          minWidth={260}
          style={{ color: "#fff", textAlign: "center" }}
        >
          <Typography
            variant="subtitle2"
            style={{ fontWeight: "bold", marginBottom: 8, color: "#fff", textAlign: "center" }}
          >
            {t("Map Legend")}
          </Typography>

          {/* Connection Status */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={2}
            p={1}
            borderRadius={1}
            bgcolor={isRealtimeActive ? "rgba(76, 175, 80, 0.2)" : "rgba(244, 67, 54, 0.2)"}
            border={1}
            borderColor={isRealtimeActive ? "rgba(76, 175, 80, 0.5)" : "rgba(244, 67, 54, 0.5)"}
          >
            <Box width={8} height={8} borderRadius="50%" bgcolor={isRealtimeActive ? "#4CAF50" : "#F44336"} mr={1} />
            <Typography variant="caption" style={{ color: "#fff" }}>
              {isRealtimeActive ? t("Real-time updates active") : t("Real-time updates inactive")}
            </Typography>
          </Box>
          {routes.length > 1 && (
            <FormControl fullWidth size="small" style={{ marginBottom: 12 }}>
              <InputLabel id="legend-tour-select-label" style={{ color: "#fff" }}>
                {t("Select Tour")}
              </InputLabel>
              <Select
                labelId="legend-tour-select-label"
                value={selectedLegendTour || routes[0].id}
                onChange={(e) => setSelectedLegendTour(e.target.value)}
                style={{ color: "#fff", background: "#222", borderRadius: 4 }}
                MenuProps={{ PaperProps: { style: { background: "#222", color: "#fff" } } }}
              >
                {routes.map((route) => (
                  <MenuItem key={route.id} value={route.id}>
                    {`T${route.uuid} - ${route.Tour?.name || ""}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {routes.length > 1 && (
            <>
              <LegendItem
                color={getColor(selectedLegendTour || routes[0].id, "driver", "#A259FF")}
                label={t("Driven Route")}
                visible={showDriverPath}
                onToggle={() => setShowDriverPath((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: selectedLegendTour || routes[0].id,
                    type: "driver",
                    tempColor: getColor(selectedLegendTour || routes[0].id, "driver", "#A259FF"),
                  })
                }
              />
              {isRealtimeActive &&
                temporaryPathGeometry &&
                temporaryPathGeometry.features &&
                temporaryPathGeometry.features.length > 0 && (
                  <LegendItem
                    color={getColor(selectedLegendTour || routes[0].id, "realTime", "#B266FF")}
                    label={t("Real-Time Path")}
                    visible={showRealTimePath}
                    onToggle={() => setShowRealTimePath((v) => !v)}
                    onColorClick={() =>
                      setColorPicker({
                        open: true,
                        routeId: selectedLegendTour || routes[0].id,
                        type: "realTime",
                        tempColor: getColor(selectedLegendTour || routes[0].id, "realTime", "#B266FF"),
                      })
                    }
                  />
                )}
              <LegendItem
                color={getColor(selectedLegendTour || routes[0].id, "navigation", "#6F9CEB")}
                label={t("Suggested Route")}
                visible={showNavigation}
                onToggle={() => setShowNavigation((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: selectedLegendTour || routes[0].id,
                    type: "navigation",
                    tempColor: getColor(selectedLegendTour || routes[0].id, "navigation", "#6F9CEB"),
                  })
                }
              />
              <LegendItem
                color={getColor(selectedLegendTour || routes[0].id, "estimated", "#ff9900")}
                label={t("Planned Route")}
                visible={showEstimated}
                onToggle={() => setShowEstimated((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: selectedLegendTour || routes[0].id,
                    type: "estimated",
                    tempColor: getColor(selectedLegendTour || routes[0].id, "estimated", "#ff9900"),
                  })
                }
              />
            </>
          )}
          {routes.length <= 1 && (
            <>
              <LegendItem
                color={getColor(routes[0]?.id, "driver", "#A259FF")}
                label={t("Driven Route")}
                visible={showDriverPath}
                onToggle={() => setShowDriverPath((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: routes[0]?.id,
                    type: "driver",
                    tempColor: getColor(routes[0]?.id, "driver", "#A259FF"),
                  })
                }
              />
              {isRealtimeActive &&
                temporaryPathGeometry &&
                temporaryPathGeometry.features &&
                temporaryPathGeometry.features.length > 0 && (
                  <LegendItem
                    color={getColor(routes[0]?.id, "realTime", "#B266FF")}
                    label={t("Real-Time Path")}
                    visible={showRealTimePath}
                    onToggle={() => setShowRealTimePath((v) => !v)}
                    onColorClick={() =>
                      setColorPicker({
                        open: true,
                        routeId: routes[0]?.id,
                        type: "realTime",
                        tempColor: getColor(routes[0]?.id, "realTime", "#B266FF"),
                      })
                    }
                  />
                )}
              <LegendItem
                color={getColor(routes[0]?.id, "navigation", "#6F9CEB")}
                label={t("Suggested Route")}
                visible={showNavigation}
                onToggle={() => setShowNavigation((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: routes[0]?.id,
                    type: "navigation",
                    tempColor: getColor(routes[0]?.id, "navigation", "#6F9CEB"),
                  })
                }
              />
              <LegendItem
                color={getColor(routes[0]?.id, "estimated", "#ff9900")}
                label={t("Planned Route")}
                visible={showEstimated}
                onToggle={() => setShowEstimated((v) => !v)}
                onColorClick={() =>
                  setColorPicker({
                    open: true,
                    routeId: routes[0]?.id,
                    type: "estimated",
                    tempColor: getColor(routes[0]?.id, "estimated", "#ff9900"),
                  })
                }
              />
            </>
          )}
          {colorPicker.open && (
            <Box mt={2}>
              <ColorPicker
                value={colorPicker.tempColor}
                onChange={(c) => setColorPicker((cp) => ({ ...cp, tempColor: c }))}
                onOk={(c) => {
                  setCustomColors((prev) => ({
                    ...prev,
                    [colorPicker.routeId]: {
                      ...prev[colorPicker.routeId],
                      [colorPicker.type]: c,
                    },
                  }));
                  setColorPicker({ open: false, routeId: null, type: null, tempColor: "" });
                }}
                onCancel={() => setColorPicker({ open: false, routeId: null, type: null, tempColor: "" })}
                dark
              />
            </Box>
          )}
        </Box>
      )}
    </ReactMapGL>
  );
};
